import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

/**
 * Check if the error is a refresh token related error that should be suppressed.
 * Refresh token expiry/revocation is expected behavior (user gets logged out),
 * and should not be reported to Sentry.
 */
function isRefreshTokenError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    const url = error.config?.url;
    const status = error.response?.status;
    // Suppress 400 and 403 errors from the /auth/refresh endpoint
    // 400 = missing/invalid refresh token (user not logged in or session corrupted)
    // 403 = refresh token expired/revoked (expected session expiry)
    // These are expected errors during logout flow and should not be reported to Sentry
    if (url?.includes('/auth/refresh') && (status === 400 || status === 403)) {
      return true;
    }
  }
  return false;
}

function isChameleonTourSaveError(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  const errorValue = exception?.value || '';
  const stackFrames = exception?.stacktrace?.frames || [];
  const hasChameleonFrame = stackFrames.some((frame) => (
    frame.filename?.includes('assets/chmln') ||
    frame.filename?.includes('chmln') ||
    frame.function?.includes('onTourSaveError')
  ));

  return errorValue.includes('Cannot read properties of undefined (reading \'join\')') &&
    hasChameleonFrame;
}

export function initSentry() {
  if (!process.env.REACT_APP_SENTRY_DSN || process.env.REACT_APP_ENVIRONMENT === 'dev') {
    // Skip if SENTRY_DSN is not set. e.g. localhost
    return;
  }
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT,
    release: process.env.REACT_APP_GIT_SHA ? `optimus-react@${process.env.REACT_APP_GIT_SHA}` : undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.5, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/api(.*)\.optimusapp\.io/,
    ],
    // Session Replay
    // This sets the sample rate at 10%. You may want to change it to 100%
    // while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: 0.01,
    // If you're not already sampling the entire session,
    // change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Suppress refresh token expiry/revocation errors from being reported to Sentry.
      // These are expected when a user's session has expired and they get logged out.
      if (isRefreshTokenError(hint?.originalException)) {
        return null;
      }
      if (isChameleonTourSaveError(event)) {
        return null;
      }
      return event;
    },
  });
}
